import React, { useState, useEffect, useMemo } from "react";
import DocumentTitle from "react-document-title";
import { FaAngleRight, FaPencilAlt, FaPlus } from "react-icons/fa";
import { FaSpinner, FaTrash, FaXmark } from "react-icons/fa6";

import SectionTable, { SectionHead, SectionBody } from "styles/components/SectionTable";

import { getAccessToken } from "utilities/localStore";

import styled from "styled-components";
import { useDispatch } from "react-redux";
import { addNotification, openModal } from "store/state/ui/actions";
import { MODAL_CONFIRMATION } from "lib/constants";
import { createNotification, LEVELS } from "utilities/notification";
import { restApiBaseUrl } from "constants/api";

const apiUrl = restApiBaseUrl;

const NavigatorContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 100%;
  width: 100%;
  cursor: default;
  position: relative;
`;

const NavigatorAction = styled.div`
  position: absolute;
  top: -32px;
  left: 0px;
  z-index: 10;
  font-weight: normal;
  font-size: 12px;
  padding: 3px 8px;
  padding-right: 9px;
  padding-bottom: 4px;
  border-radius: 3px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  background-color: #484848;
  color: #fff;

  @media screen and (max-width: 865px) {
    margin-left: 0px;
  }

  margin-left: calc((100vw - 865px) / 2);

  :hover {
    background-color: #333;
  }

  svg {
    margin-bottom: -1px;
    font-weight: normal;
  }
`;

const LoadingIndicator = styled(NavigatorAction)`
  left: 125px;
  gap: 8px;
  background-color: transparent;
  color: #333;
  pointer-events: none;

  svg {
    animation: spin 1s linear infinite;
  }
`;

const NavigatorColumn = styled.div`
  width: 100%;
  max-width: 650px;
  height: 100%;
  border-top: 1px solid #b8c2c7;
  border-bottom: 1px solid #b8c2c7;
  border-left: 1px solid #b8c2c7;
  padding-bottom: 50px;
  text-align: left;
  padding-top: 12px;
  padding-bottom: 12px;
  position: relative;
  contain: strict;

  :first-child {
    border-top-left-radius: 3px;
    border-bottom-left-radius: 3px;
  }

  :last-child {
    border-right: 1px solid #b8c2c7;
    border-top-right-radius: 3px;
    border-bottom-right-radius: 3px;
  }
`;

const NavigatorColumnTitle = styled.div`
  position: sticky;
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 10px;
  padding: 0px 16px;
  padding-bottom: 10px;
  border-bottom: 1px solid #b8c2c7;
`;

const NavigatorColumnContent = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  contain: strict;
`;

const NavigatorGroup = styled.div`
  padding: 8px 10px;

  :last-child {
    margin-bottom: 75px;
  }
`;

const NavigatorGroupAction = styled.div`
  font-weight: normal;
  font-size: 12px;
  margin-left: 8px;
  color: #484848;
  padding: 6px;
  border-radius: 2px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    margin-bottom: -1px;
    font-weight: normal;
  }

  :hover {
    background-color: #484848;
    color: #fff;
  }
`;

const NavigatorChild = styled.div`
  font-size: 14px;
  color: #696969;
  margin-bottom: 6px;
  padding: 8px 12px;
  border-radius: 2px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  :hover {
    background-color: #484848;
    color: #fff;
  }

  ${(props) =>
    props.isDisabled &&
    `
    pointer-events: none;
    font-weight: normal;
    `}

${(props) =>
  props.isActive &&
  `
    background-color: #484848;
    color: #fff;
  `}

${(props) =>
  props.disableHover &&
  `
    :hover {
      background-color: transparent;
      color: #696969;
    }
  `}
`;

const TopicForm = styled.form`
  display: block;
  display: flex;
  align-items: center;
  width: 95%;
  max-width: 95%;
  margin-left: 10px;
  margin-bottom: 10px;
  gap: 2px;
`;

const TopicFormInput = styled.input`
  display: block;
  width: 200px;
  outline: none;
  border: 1px solid #b8c2c7;
  height: fit-content;
  padding: 6px 12px;
  border-radius: 2px;
  box-sizing: border-box;
`;

const TopicFormSelect = styled.select`
  display: block;
  width: 200px;
  flex-grow: 1;
  outline: none;
  border: 1px solid #b8c2c7;
  height: fit-content;
  padding: 6px 12px;
  border-radius: 2px;
  box-sizing: border-box;
`;

const TopicFormButton = styled.button`
  display: block;
  outline: none;
  border: none;
  flex-grow: 0.2;
  height: fit-content;
  padding: 9px 12px;
  padding-bottom: 10px;
  border-radius: 2px;
  background-color: #484848;
  color: #fff;
  font-size: 12px;

  :hover {
    background-color: #333;
  }

  :disabled {
    pointer-events: none;
    opacity: 0.7;
  }
`;

const Form = styled.form`
  display: block;
  display: flex;
  align-items: center;
  width: 90%;
  gap: 2px;
`;

const FormInput = styled.input`
  display: block;
  width: 100%;
  outline: none;
  border: 1px solid #b8c2c7;
  padding: 5px 8px;
  font-size: 14px;
  border-radius: 2px;
  box-sizing: border-box;
`;

const FormSelect = styled.select`
  display: block;
  width: 100%;
  outline: none;
  border: 1px solid #b8c2c7;
  padding: 4px 8px;
  font-size: 14px;
  border-radius: 2px;
  box-sizing: border-box;
`;

const FormButton = styled.button`
  display: block;
  outline: none;
  border: none;
  padding: 7px 8px;
  padding-bottom: 8px;
  border-radius: 2px;
  background-color: #484848;
  color: #fff;
  font-size: 12px;

  :hover {
    background-color: #333;
  }

  :disabled {
    pointer-events: none;
    opacity: 0.7;
  }
`;

const AdminPage = () => {
  const dispatch = useDispatch();

  const [topics, setTopics] = useState([]);
  const [tags, setTags] = useState([]);
  const [draftTopic, setDraftTopic] = useState("");
  const [draftTopicTag, setDraftTopicTag] = useState("");
  const [editingTopicId, setEditingTopicId] = useState(null);
  const [showTopicForm, setShowTopicForm] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState("");

  // Fetch accounts on mount
  useEffect(() => {
    setLoadingMessage("Fetching topics...");

    const tagsUrl = `${apiUrl}/rest/tags`;
    fetch(tagsUrl, {
      mode: "cors",
      method: "get",
      headers: {
        Authorization: getAccessToken(),
      },
    })
      .then((response) => response.json())
      .then((result) => {
        setTags(result.data);
      });

    const topicsUrl = `${apiUrl}/rest/topics`;
    fetch(topicsUrl, {
      mode: "cors",
      method: "get",
      headers: {
        Authorization: getAccessToken(),
      },
    })
      .then((response) => response.json())
      .then((result) => {
        setTopics(result.data);
        setLoadingMessage("");
      });
  }, []);

  const resetForms = () => {
    setDraftTopic("");
    setDraftTopicTag("");
    setEditingTopicId(null);
    setShowTopicForm(false);
  };

  const editTopic = (topicId) => {
    resetForms();
    setDraftTopic(topics.find((t) => t.id === topicId).name);
    setDraftTopicTag(topics.find((t) => t.id === topicId).tags[0]?.id);
    setEditingTopicId(topicId);
  };

  const deleteTopic = (topicId) => {
    dispatch(
      openModal({
        type: MODAL_CONFIRMATION,
        data: {
          id: topicId,
          title: topics.find((t) => t.id === topicId)?.name || "this topic",
          icon: "ico-unit-delete.svg",
          type: "delete",
          action: () => {
            setLoadingMessage("Deleting topic...");

            const url = `${apiUrl}/rest/topics/${topicId}`;
            fetch(url, {
              mode: "cors",
              method: "delete",
              headers: {
                Authorization: getAccessToken(),
                "Content-Type": "application/json",
              },
            })
              .then((response) => {
                return response.json();
              })
              .then((result) => {
                console.log("[delete][topic]", { result });

                if (result.status === "error") {
                  dispatch(
                    addNotification({
                      notification: createNotification(
                        LEVELS.ERROR,
                        "Delete Topic",
                        result.message || "System Error"
                      ),
                    })
                  );
                } else {
                  dispatch(
                    addNotification({
                      notification: createNotification(
                        LEVELS.SUCCESS,
                        "Delete Topic",
                        result.message || "Success"
                      ),
                    })
                  );
                  setTopics((previous) => previous.filter((p) => p.id !== topicId));
                }
                setLoadingMessage("");
                resetForms();
              });
          },
        },
      })
    );
  };

  return (
    <DocumentTitle title="Topic Manager | Stemify">
      <SectionTable
        style={{
          padding: "10px",
          height: "calc(100vh - 125px)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <SectionHead style={{ marginBottom: "10px", overflow: "visible" }}>
          <h2 style={{ textAlign: "center" }}>Topic Manager</h2>
        </SectionHead>

        <SectionBody style={{ padding: "50px 100px", height: "100%", flexGrow: 1 }}>
          <NavigatorContainer>
            <NavigatorAction
              onClick={() => {
                resetForms();
                setShowTopicForm(true);
              }}
            >
              <FaPlus size={10} />
              <span>Add Topic</span>
            </NavigatorAction>
            {loadingMessage && (
              <LoadingIndicator>
                <FaSpinner className="fa-spin" />
                <span>{loadingMessage}</span>
              </LoadingIndicator>
            )}

            <NavigatorColumn>
              <NavigatorColumnTitle>Topics</NavigatorColumnTitle>

              <NavigatorColumnContent>
                {showTopicForm && (
                  <TopicForm
                    onSubmit={(event) => {
                      event.preventDefault();
                      setLoadingMessage("Creating topic...");

                      const url = `${apiUrl}/rest/topics`;
                      fetch(url, {
                        mode: "cors",
                        method: "post",
                        headers: {
                          Authorization: getAccessToken(),
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                          name: draftTopic,
                          tagId: draftTopicTag || null,
                        }),
                      })
                        .then((response) => response.json())
                        .then((result) => {
                          if (result.status === "error") {
                            dispatch(
                              addNotification({
                                notification: createNotification(
                                  LEVELS.ERROR,
                                  "Create Topic",
                                  result.message || "System Error"
                                ),
                              })
                            );
                          } else {
                            dispatch(
                              addNotification({
                                notification: createNotification(
                                  LEVELS.SUCCESS,
                                  "Create Topic",
                                  result.message || "Success"
                                ),
                              })
                            );

                            setTopics((previous) => {
                              console.log({ previous });
                              console.log(result.data);

                              return previous.concat(result.data);
                            });
                          }

                          setLoadingMessage("");
                          resetForms();
                        })
                        .catch((error) => console.error(error));
                    }}
                  >
                    <TopicFormInput
                      type="text"
                      autoFocus
                      placeholder="Topic name..."
                      value={draftTopic}
                      onChange={(event) => {
                        setDraftTopic(event.target.value);
                      }}
                    />
                    <TopicFormSelect
                      value={draftTopicTag}
                      onChange={(event) => {
                        setDraftTopicTag(event.target.value);
                      }}
                      placeholder="Select a tag..."
                    >
                      <option value="" disabled selected>
                        Select a tag...
                      </option>
                      {tags.map((tag) => {
                        return (
                          <option key={tag.id} value={tag.id}>
                            {tag.name}
                          </option>
                        );
                      })}
                    </TopicFormSelect>
                    <TopicFormButton disabled={draftTopic.length === 0 || !draftTopicTag}>
                      Create
                    </TopicFormButton>
                  </TopicForm>
                )}

                <NavigatorGroup>
                  {topics.length > 0 ? (
                    topics.map((topic) => {
                      if (editingTopicId === topic.id) {
                        return (
                          <Form
                            key={topic.id}
                            onSubmit={(event) => {
                              event.preventDefault();
                              setLoadingMessage("Updating topic...");

                              const url = `${apiUrl}/rest/topics/${editingTopicId}`;
                              fetch(url, {
                                mode: "cors",
                                method: "put",
                                headers: {
                                  Authorization: getAccessToken(),
                                  "Content-Type": "application/json",
                                },
                                body: JSON.stringify({
                                  name: draftTopic,
                                  tagId: draftTopicTag,
                                }),
                              })
                                .then((response) => response.json())
                                .then((result) => {
                                  setTopics((previous) =>
                                    previous.map((t) => {
                                      if (t.id === editingTopicId) {
                                        return {
                                          ...t,
                                          ...result.data,
                                        };
                                      }

                                      return t;
                                    })
                                  );
                                  setLoadingMessage("");
                                  resetForms();
                                });
                            }}
                          >
                            <FormInput
                              type="text"
                              autoFocus
                              value={draftTopic}
                              onChange={(event) => {
                                setDraftTopic(event.target.value);
                              }}
                            />
                            <FormSelect
                              value={draftTopicTag}
                              onChange={(event) => {
                                setDraftTopicTag(event.target.value);
                              }}
                              placeholder="Select a tag..."
                            >
                              <option value="" disabled selected>
                                Select a tag...
                              </option>
                              {tags.map((tag) => {
                                return (
                                  <option key={tag.id} value={tag.id}>
                                    {tag.name}
                                  </option>
                                );
                              })}
                            </FormSelect>
                            <FormButton disabled={draftTopic.length === 0}>Update</FormButton>
                            <FormButton onClick={resetForms}>
                              <FaXmark size={10} />
                            </FormButton>
                          </Form>
                        );
                      }

                      return (
                        <NavigatorChild key={topic.id} disableHover={true}>
                          <div>
                            <div style={{ fontWeight: "bold", fontSize: "16px" }}>{topic.name}</div>
                            <div>
                              Tag: {topic.tags?.length > 0 ? topic.tags[0].name : "No tag assigned"}
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                              width: "100px",
                              marginBottom: "4px",
                            }}
                          >
                            <NavigatorGroupAction
                              title="Edit Topic"
                              onClick={() => {
                                editTopic(topic.id);
                              }}
                            >
                              <FaPencilAlt />
                            </NavigatorGroupAction>

                            <NavigatorGroupAction
                              title="Delete Topic"
                              onClick={() => {
                                deleteTopic(topic.id);
                              }}
                            >
                              <FaTrash size={12} />
                            </NavigatorGroupAction>
                          </div>
                        </NavigatorChild>
                      );
                    })
                  ) : (
                    <NavigatorChild isDisabled={true}>Loading topics..</NavigatorChild>
                  )}
                </NavigatorGroup>
              </NavigatorColumnContent>
            </NavigatorColumn>
          </NavigatorContainer>
        </SectionBody>
      </SectionTable>
    </DocumentTitle>
  );
};

export default AdminPage;
